import type {
	NoteContentInfo,
	NoteFolderInfo,
	NoteInfo,
	NotePopulated,
	NoteTitleInfo,
	NoteStreamingChunk,
	Note,
	Share,
	ApiResponse,
	UniqueId,
} from "@vita-learning/types";

import { makeUrl, revalidate } from "~/shared/utils";

import { type HttpConfig, aiHttpClient, resHttpClient } from "../../config";
import { assertCorrectUrlEndpoint } from "../../utils";

import { decodeNote } from "./helpers";

export const createNote = async (note: NoteInfo): Promise<NotePopulated> => {
	const resp = await resHttpClient.post<ApiResponse<NotePopulated>>(
		"/notes",
		note
	);

	if (!resp.success) throw resp.error;

	await revalidate("notes");

	return decodeNote(resp.data);
};

export const createManyNotes = async (
	notes: NoteInfo[]
): Promise<NotePopulated[]> => {
	const resp = await resHttpClient.post<ApiResponse<NotePopulated[]>>(
		"/notes?bulk=true",
		notes
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("notes");

	return resp.data.map(decodeNote);
};

export const createNoteShareUrl = async (
	payload: UniqueId
): Promise<{ url: string }> => {
	const resp = await resHttpClient.post<ApiResponse<{ url: string }>>(
		"/notes/share-urls",
		payload
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	return resp.data;
};

export const getNote = async (
	id: string,
	config?: HttpConfig
): Promise<NotePopulated> => {
	const resp = await resHttpClient.get<ApiResponse<NotePopulated>>(
		`/notes/${id}`,
		config
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	return decodeNote(resp.data);
};

export const fetchNotes = async (
	url: string,
	config?: HttpConfig
): Promise<NotePopulated[]> => {
	assertCorrectUrlEndpoint(url, "/notes");

	const resp = await resHttpClient.get<ApiResponse<NotePopulated[] | null>>(
		url,
		config
	);

	if (!resp.success) throw resp.error;

	return (resp.data || []).map(decodeNote);
};

export const updateNote = async (note: NoteInfo): Promise<NotePopulated> => {
	if (!note.id) throw new Error("Note ID is required");

	const resp = await resHttpClient.put<ApiResponse<NotePopulated>>(
		`/notes/${note.id}`,
		note
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("notes");

	return decodeNote(resp.data);
};

export const shareNote = async (
	id: string,
	share: Share
): Promise<NotePopulated> => {
	const resp = await resHttpClient.patch<ApiResponse<NotePopulated>>(
		`/notes/${id}/share`,
		{ ...share, id }
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("notes");

	return decodeNote(resp.data);
};

export const modifyNoteFolder = async (
	noteFolder: NoteFolderInfo
): Promise<NotePopulated> => {
	const resp = await resHttpClient.patch<ApiResponse<NotePopulated>>(
		`/notes/${noteFolder.id}/folderId`,
		noteFolder
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("notes");

	return decodeNote(resp.data);
};

export const modifyNoteTitle = async (
	noteTitle: NoteTitleInfo
): Promise<NotePopulated> => {
	const resp = await resHttpClient.patch<ApiResponse<NotePopulated>>(
		`/notes/${noteTitle.id}/title`,
		noteTitle
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("notes");

	return decodeNote(resp.data);
};

export const syncNoteContent = async (
	content: NoteContentInfo
): Promise<void> => {
	const resp = await resHttpClient.patch<ApiResponse<void>>(
		`/notes/${content.id}/content`,
		content
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("notes");

	return resp.data;
};

/**
 * Automatically generate a note from a file, URL, or prompt
 * @param {FormData} note the formdata containing the file
 * @param {"file" | "url" | "prompt"} source the source of the note
 * @returns {Promise<void>}
 */
export const generateNote = (
	note: Partial<NoteInfo>,
	source: Extract<Note["source"], "file" | "url" | "prompt">,
	onStream: (chunk: NoteStreamingChunk) => void
) => {
	const url = makeUrl("/notes");

	url.searchParams.set("source", source);

	return aiHttpClient.post<void>(url.pathname + url.search, note, {
		headers: {
			// Accept server-sent events
			Accept: "text/event-stream",
			// Disabling compression for streaming responses
			"Accept-Encoding": "identity",
			// Sending the data as a multipart form
			"Content-Type": "application/json",
			// Preventing caching
			"Cache-Control": "no-cache",
		},
		cache: "no-cache",
		onStream: chunk => {
			// Chunks might accumulate so split them by the new line
			const lines = chunk.split("\n").filter(line => line.trim() !== "");
			const DATA_CHUNK_PREFIX = "data: ";
			const ERROR_CHUNK_PREFIX = "error: ";

			lines.forEach(line => {
				try {
					if (line.startsWith(DATA_CHUNK_PREFIX))
						onStream(
							JSON.parse(
								line.replace(DATA_CHUNK_PREFIX, "").trim()
							) as NoteStreamingChunk
						);

					if (line.startsWith(ERROR_CHUNK_PREFIX))
						throw JSON.parse(line.replace(ERROR_CHUNK_PREFIX, "").trim());
				} catch (e) {
					if (!(e instanceof SyntaxError)) throw e;

					console.error(e);
				}
			});
		},
	});
};

export const deleteNote = async (id: string) => {
	const data = await resHttpClient.delete<void>(`/notes/${id}`);

	await revalidate("notes");
	return data;
};
